import { $insertList, $isListItemNode, $isListNode, ListNode } from '@lexical/list';
import { $findMatchingParent, mergeRegister } from '@lexical/utils';
import type { DOMConversionMap, DOMConversionOutput, DOMExportOutput, HTMLConfig, LexicalEditor, LexicalNode } from 'lexical';
import { $getNodeByKey, $getSelection, $getState, $isRangeSelection, $setState, createState } from 'lexical';

/** How a numbered list counts: `1. 2. 3.` at every level, or by path (`1.`, `1.1`, `1.2.1`). */
export type ListNumbering = 'flat' | 'hierarchical';

/** The saved format: the outermost `<ol>` carries it, so it survives Froala, the sanitiser and paste. */
const NUMBERING_ATTRIBUTE = 'data-lexical-numbering';

/**
 * Lives on the outermost list only; nested lists follow it through CSS.
 * - Lexical copies node state when it splits or indents a list, so the flag isn't lost there.
 */
const numberingState = createState('numbering', {
  parse: (value): ListNumbering => (value === 'hierarchical' ? 'hierarchical' : 'flat'),
});

/**
 * Path numbering via CSS counters, for the outermost `<ol>`.
 * - Every `<ol>` restarts the count; `counters()` joins the levels.
 * - Wrapper items (holding only a nested list) neither count nor show a number.
 * - The marker hangs left of the text, like the browser's own, so wrapped lines stay aligned.
 */
const HIERARCHICAL_CLASSES = [
  'list-none! [counter-reset:pbk-list] [&_ol]:list-none [&_ol]:[counter-reset:pbk-list]',
  '[&_li]:relative [&_li]:before:absolute [&_li]:before:right-full [&_li]:before:pr-[0.4em] [&_li]:before:whitespace-nowrap',
  '[&>li:not(:has(>*>li))]:[counter-increment:pbk-list] [&>li:not(:has(>*>li))]:before:content-[counters(pbk-list,".")_"."]',
  '[&_ol>li:not(:has(>*>li))]:[counter-increment:pbk-list] [&_ol>li:not(:has(>*>li))]:before:content-[counters(pbk-list,".")]',
]
  .join(' ')
  .split(' ');

const isHierarchical = (list: ListNode) => list.getListType() === 'number' && $getState(list, numberingState) === 'hierarchical';

const setAttribute = (element: HTMLElement, hierarchical: boolean) => {
  if (hierarchical) element.setAttribute(NUMBERING_ATTRIBUTE, 'hierarchical');
  else element.removeAttribute(NUMBERING_ATTRIBUTE);
};

const $getOutermostList = (node: LexicalNode): ListNode | null => {
  let list = $findMatchingParent(node, $isListNode);
  for (let parent = list; parent; parent = $findMatchingParent(parent.getParentOrThrow(), $isListNode)) list = parent;
  return list;
};

/** The numbering of the list holding `node`; `null` outside a numbered list. */
export const $getListNumbering = (node: LexicalNode): ListNumbering | null => {
  const list = $getOutermostList(node);
  return list?.getListType() === 'number' ? $getState(list, numberingState) : null;
};

/** Turns the selected blocks into a numbered list (if they aren't one) counting the given way. */
export const $setListNumbering = (numbering: ListNumbering) => {
  let selection = $getSelection();
  if (!$isRangeSelection(selection)) return;

  if (selection.getNodes().some((node) => $getListNumbering(node) === null)) {
    $insertList('number');
    selection = $getSelection();
    if (!$isRangeSelection(selection)) return;
  }

  const lists = new Set(selection.getNodes().map($getOutermostList));
  for (const list of lists) if (list) $setState(list, numberingState, numbering);
};

/**
 * Reads the attribute back on load and paste, on top of `ListNode`'s own `<ol>` import.
 * - `ListNode.importDOM` is looked up per element: Lexical only fills it in once an editor registers the node.
 */
const importConfig: DOMConversionMap = {
  ol: (element) => {
    if (element.getAttribute(NUMBERING_ATTRIBUTE) !== 'hierarchical') return null;
    const listImport = ListNode.importDOM?.()?.ol?.(element);
    if (!listImport) return null;

    return {
      priority: 1,
      conversion: (domNode): DOMConversionOutput | null => {
        const output = listImport.conversion(domNode);
        const node = output?.node;
        if (!Array.isArray(node) && $isListNode(node)) $setState(node, numberingState, 'hierarchical');
        return output;
      },
    };
  },
};

const exportList = (editor: LexicalEditor, node: LexicalNode): DOMExportOutput => {
  const list = node as ListNode;
  const output = list.exportDOM(editor);
  // The attribute only: the classes are editor styling, and nothing else renders it yet
  if (output.element instanceof HTMLElement) setAttribute(output.element, isHierarchical(list));
  return output;
};

/** Composer `html` config: the numbering's HTML import and export. */
export const LIST_NUMBERING_HTML: HTMLConfig = {
  import: importConfig,
  export: new Map([[ListNode, exportList]]),
};

/**
 * Keeps the flag on outermost numbered lists only and mirrors it into the editor's DOM.
 * - Indenting copies the outer list (state included) into the nested one, so the copy is cleared.
 * - Converting to bullets or a check list drops it, so converting back gives a plain numbered list.
 */
export const registerListNumbering = (editor: LexicalEditor): (() => void) =>
  mergeRegister(
    editor.registerNodeTransform(ListNode, (list) => {
      if ($getState(list, numberingState) === 'flat') return;
      if (list.getListType() !== 'number' || $isListItemNode(list.getParent())) $setState(list, numberingState, 'flat');
    }),
    editor.registerMutationListener(ListNode, (mutations) => {
      editor.getEditorState().read(() => {
        for (const [key, mutation] of mutations) {
          if (mutation === 'destroyed') continue;
          const list = $getNodeByKey(key);
          const element = editor.getElementByKey(key);
          if (!$isListNode(list) || !element) continue;
          const hierarchical = isHierarchical(list);
          setAttribute(element, hierarchical);
          for (const className of HIERARCHICAL_CLASSES) element.classList.toggle(className, hierarchical);
        }
      });
    }),
  );
